import { useMemo, useState } from "react";
import { CATEGORIES, fanWorks, type Category, type FanWork } from "@/mocks/naruto";

function CategoryCard({ work }: { work: FanWork }) {
  return (
    <article className="hover-lift flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3">
      <div className="h-28 w-20 shrink-0 overflow-hidden rounded-md sm:h-32 sm:w-24">
        <img
          src={work.cover}
          alt={`${work.title} 火影同人动漫`}
          title={`${work.title} 火影同人动漫在线`}
          className="h-full w-full object-cover object-top"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2">
          <h3 className="truncate font-heading text-sm font-bold text-foreground-900 md:text-base">
            {work.title}
          </h3>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${
              work.status === "连载中"
                ? "bg-primary-100 text-primary-700"
                : "bg-secondary-100 text-secondary-700"
            }`}
          >
            {work.status}
          </span>
        </div>
        <p className="mt-1.5 line-clamp-2 flex-1 text-xs leading-relaxed text-foreground-600 md:text-sm">
          {work.intro}
        </p>
        <div className="mt-2 flex items-center justify-between text-[11px] text-foreground-500 md:text-xs">
          <span className="flex items-center gap-1">
            <i className="ri-book-open-line"></i>
            {work.chapters}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {work.views}
          </span>
        </div>
      </div>
    </article>
  );
}

export default function Categories() {
  const [active, setActive] = useState<Category | "全部">("全部");

  const filtered = useMemo(() => {
    if (active === "全部") return fanWorks;
    return fanWorks.filter((w) => w.category === active);
  }, [active]);

  return (
    <section id="categories" className="bg-background-100 py-14 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 text-sm font-bold text-primary-600">
            <i className="ri-grid-fill"></i> 题材导航
          </span>
          <h2 className="mt-2 font-heading text-2xl font-black text-foreground-900 md:text-4xl">
            按题材浏览同人作品
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">
            剧情、穿越、系统、重生、战斗，五大热门题材精准分类，快速找到合你口味的那一部。
          </p>
        </div>

        {/* 分类标签切换 */}
        <div className="mt-8 flex flex-wrap justify-center gap-2">
          <button
            type="button"
            onClick={() => setActive("全部")}
            className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${
              active === "全部"
                ? "bg-primary-500 text-background-50"
                : "bg-background-50 text-foreground-700 hover:bg-primary-100"
            }`}
          >
            全部
          </button>
          {CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              type="button"
              onClick={() => setActive(cat.key)}
              className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                active === cat.key
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50 text-foreground-700 hover:bg-primary-100"
              }`}
            >
              <i className={`${cat.icon} mr-1`}></i>
              {cat.key}
            </button>
          ))}
        </div>

        {/* 当前分类说明 */}
        <div className="mx-auto mt-5 flex max-w-md items-center justify-center gap-2 rounded-lg bg-background-50 px-4 py-2 text-center text-xs text-foreground-500 md:text-sm">
          <i className="ri-information-line text-primary-500"></i>
          {active === "全部"
            ? "为你展示全题材精选同人作品"
            : CATEGORIES.find((c) => c.key === active)?.desc}
        </div>

        {/* 作品网格 */}
        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 md:gap-5">
          {filtered.map((work) => (
            <CategoryCard key={work.id} work={work} />
          ))}
        </div>
      </div>
    </section>
  );
}